<div data-spartan-page class="p-6 md:p-8 space-y-6"
     style="background: rgb(var(--ds-bg, 250 250 249)); min-height: 100%;">

  <!-- Page header -->
  <app-page-header
    eyebrow="Forwarding"
    title="Forwarding overview"
    subtitle="Quotation pipeline metrics and recent inquiries."
  >
    <button actions hlmBtn variant="outline" size="sm" class="gap-1.5" (click)="loadDashboardData()">
      <ng-icon hlm name="lucideRefreshCw" class="size-4" />
      Refresh
    </button>
  </app-page-header>

  <!-- KPI strip — 4 tiles with icon + value + label + hint -->
  <div class="grid grid-cols-2 lg:grid-cols-4 gap-4">

    <!-- Pending Inquiries -->
    <div class="rounded-lg border border-border bg-surface shadow-sm">
      <div class="p-5 flex items-start justify-between gap-3">
        <div class="min-w-0">
          <div class="text-xs font-medium text-muted-foreground">Pending Inquiries</div>
          <div class="text-2xl font-semibold tabular-nums tracking-tight mt-1.5">{{ pendingInquiries }}</div>
          <div class="flex items-center gap-1 mt-1.5">
            <span class="text-[11px] text-muted-foreground">This week</span>
          </div>
        </div>
        <span class="grid place-items-center w-9 h-9 rounded-md shrink-0"
              style="background-color: rgb(var(--ds-accent-light, 239 246 255)); color: rgb(var(--ds-accent, 37 99 235))">
          <ng-icon hlm name="lucideFileText" class="size-4" />
        </span>
      </div>
    </div>

    <!-- Quotes Sent -->
    <div class="rounded-lg border border-border bg-surface shadow-sm">
      <div class="p-5 flex items-start justify-between gap-3">
        <div class="min-w-0">
          <div class="text-xs font-medium text-muted-foreground">Quotes Sent</div>
          <div class="text-2xl font-semibold tabular-nums tracking-tight mt-1.5">{{ quotesSent }}</div>
          <div class="flex items-center gap-1 mt-1.5">
            <span class="text-[11px] text-muted-foreground">This month</span>
          </div>
        </div>
        <span class="grid place-items-center w-9 h-9 rounded-md shrink-0"
              style="background-color: rgb(var(--ds-accent-light, 239 246 255)); color: rgb(var(--ds-accent, 37 99 235))">
          <ng-icon hlm name="lucideSend" class="size-4" />
        </span>
      </div>
    </div>

    <!-- Awaiting Reply -->
    <div class="rounded-lg border border-border bg-surface shadow-sm">
      <div class="p-5 flex items-start justify-between gap-3">
        <div class="min-w-0">
          <div class="text-xs font-medium text-muted-foreground">Awaiting Reply</div>
          <div class="text-2xl font-semibold tabular-nums tracking-tight mt-1.5">{{ awaitingReply }}</div>
          <div class="flex items-center gap-1 mt-1.5">
            @if (overdueFollowups > 0) {
              <span class="text-[11px] font-medium text-amber-700">{{ overdueFollowups }} overdue</span>
            } @else {
              <span class="text-[11px] text-muted-foreground">All on time</span>
            }
          </div>
        </div>
        <span class="grid place-items-center w-9 h-9 rounded-md shrink-0"
              style="background-color: rgb(var(--ds-accent-light, 239 246 255)); color: rgb(var(--ds-accent, 37 99 235))">
          <ng-icon hlm name="lucideClock" class="size-4" />
        </span>
      </div>
    </div>

    <!-- Conversion Rate -->
    <div class="rounded-lg border border-border bg-surface shadow-sm">
      <div class="p-5 flex items-start justify-between gap-3">
        <div class="min-w-0">
          <div class="text-xs font-medium text-muted-foreground">Conversion Rate</div>
          <div class="text-2xl font-semibold tabular-nums tracking-tight mt-1.5">{{ conversionRate }}%</div>
          <div class="flex items-center gap-1 mt-1.5">
            <span class="text-[11px] text-muted-foreground">Last 30 days</span>
          </div>
        </div>
        <span class="grid place-items-center w-9 h-9 rounded-md shrink-0"
              style="background-color: rgb(var(--ds-accent-light, 239 246 255)); color: rgb(var(--ds-accent, 37 99 235))">
          <ng-icon hlm name="lucideTrendingUp" class="size-4" />
        </span>
      </div>
    </div>

  </div>

  <!-- Middle row: Recent Inquiries + Jump to -->
  <div class="grid grid-cols-1 lg:grid-cols-3 gap-4">

    <!-- Recent Inquiries (col-span-2) -->
    <div class="rounded-lg border border-border bg-surface shadow-sm lg:col-span-2">
      <div class="px-5 pt-4 pb-3 border-b border-border flex items-center justify-between">
        <div>
          <h3 class="text-base font-semibold leading-tight">Recent inquiries</h3>
          <p class="text-xs text-muted-foreground mt-0.5">Latest across ATR &amp; HT</p>
        </div>
        <a routerLink="../inquiries">
          <button hlmBtn variant="ghost" size="sm" class="gap-1">
            View all <ng-icon hlm name="lucideChevronRight" class="size-3.5" />
          </button>
        </a>
      </div>

      @if (recentInquiries.length === 0) {
        <div class="py-8 text-center text-sm text-muted-foreground">No recent inquiries</div>
      } @else {
        <div class="overflow-x-auto">
          <table class="w-full text-sm">
            <thead>
              <tr class="text-[11px] font-medium uppercase tracking-wider text-muted-foreground border-b border-border-strong">
                <th class="px-5 py-2.5 text-left font-medium">Inquiry #</th>
                <th class="px-4 py-2.5 text-left font-medium">Client</th>
                <th class="px-4 py-2.5 text-left font-medium">Route</th>
                <th class="px-4 py-2.5 text-left font-medium">Status</th>
              </tr>
            </thead>
            <tbody>
              @for (inquiry of recentInquiries; track inquiry.id) {
                <tr class="border-b border-border last:border-0 even:bg-surface-raised/50 hover:bg-surface-raised transition-colors cursor-pointer"
                    [routerLink]="['../inquiries', inquiry.id]">
                  <td class="px-5 h-11 font-mono text-[12.5px] font-medium"
                      style="color: rgb(var(--ds-accent, 37 99 235))">
                    {{ inquiry.inquiryNumber }}
                  </td>
                  <td class="px-4 h-11 font-medium truncate max-w-[180px]">{{ inquiry.clientName }}</td>
                  <td class="px-4 h-11 font-mono text-[12px] text-muted-foreground">
                    {{ inquiry.origin }} → {{ inquiry.destination }}
                  </td>
                  <td class="px-4 h-11">
                    <app-status-badge [variant]="getStatusVariant(inquiry.status)" [label]="inquiry.status" />
                  </td>
                </tr>
              }
            </tbody>
          </table>
        </div>
      }
    </div>

    <!-- Jump to -->
    <div class="rounded-lg border border-border bg-surface shadow-sm">
      <div class="px-5 pt-4 pb-3 border-b border-border">
        <h3 class="text-base font-semibold leading-tight">Jump to</h3>
        <p class="text-xs text-muted-foreground mt-0.5">Forwarding modules</p>
      </div>
      <div class="p-3 grid grid-cols-2 gap-2">
        <a routerLink="../inquiries/new" class="flex items-center gap-2.5 px-3 h-10 rounded-md border border-border bg-surface hover:bg-surface-raised hover:border-border-strong transition text-sm">
          <span class="grid place-items-center w-7 h-7 rounded-md shrink-0"
                style="background-color: rgb(var(--ds-accent-light, 239 246 255)); color: rgb(var(--ds-accent, 37 99 235))">
            <ng-icon hlm name="lucidePlus" class="size-3.5" />
          </span>
          <span class="text-foreground/90 font-medium truncate">New inquiry</span>
        </a>
        <a routerLink="../inquiries" class="flex items-center gap-2.5 px-3 h-10 rounded-md border border-border bg-surface hover:bg-surface-raised hover:border-border-strong transition text-sm">
          <span class="grid place-items-center w-7 h-7 rounded-md shrink-0"
                style="background-color: rgb(var(--ds-accent-light, 239 246 255)); color: rgb(var(--ds-accent, 37 99 235))">
            <ng-icon hlm name="lucideList" class="size-3.5" />
          </span>
          <span class="text-foreground/90 font-medium truncate">All inquiries</span>
        </a>
        <a routerLink="../quotes" class="flex items-center gap-2.5 px-3 h-10 rounded-md border border-border bg-surface hover:bg-surface-raised hover:border-border-strong transition text-sm">
          <span class="grid place-items-center w-7 h-7 rounded-md shrink-0"
                style="background-color: rgb(var(--ds-accent-light, 239 246 255)); color: rgb(var(--ds-accent, 37 99 235))">
            <ng-icon hlm name="lucideFileText" class="size-3.5" />
          </span>
          <span class="text-foreground/90 font-medium truncate">Quotations</span>
        </a>
        <a routerLink="../admin" class="flex items-center gap-2.5 px-3 h-10 rounded-md border border-border bg-surface hover:bg-surface-raised hover:border-border-strong transition text-sm">
          <span class="grid place-items-center w-7 h-7 rounded-md shrink-0"
                style="background-color: rgb(var(--ds-accent-light, 239 246 255)); color: rgb(var(--ds-accent, 37 99 235))">
            <ng-icon hlm name="lucideSettings" class="size-3.5" />
          </span>
          <span class="text-foreground/90 font-medium truncate">Admin</span>
        </a>
      </div>
    </div>

  </div>

  <!-- Lower row: Quotes for Follow-up -->
  <div class="rounded-lg border border-border bg-surface shadow-sm">
    <div class="px-5 pt-4 pb-3 border-b border-border flex items-center justify-between">
      <div>
        <h3 class="text-base font-semibold leading-tight">Quotes requiring follow-up</h3>
        <p class="text-xs text-muted-foreground mt-0.5">Sent and awaiting client response</p>
      </div>
      <a routerLink="../quotes">
        <button hlmBtn variant="ghost" size="sm" class="gap-1">
          View all <ng-icon hlm name="lucideChevronRight" class="size-3.5" />
        </button>
      </a>
    </div>

    @if (quotesForFollowup.length === 0) {
      <div class="py-8 text-center text-sm text-muted-foreground">No quotes requiring follow-up</div>
    } @else {
      <div class="overflow-x-auto">
        <table class="w-full text-sm">
          <thead>
            <tr class="text-[11px] font-medium uppercase tracking-wider text-muted-foreground border-b border-border-strong">
              <th class="px-5 py-2.5 text-left font-medium">Quote #</th>
              <th class="px-4 py-2.5 text-left font-medium">Client</th>
              <th class="px-4 py-2.5 text-left font-medium">Sent</th>
              <th class="px-4 py-2.5 text-left font-medium">Status</th>
              <th class="px-5 py-2.5 text-right font-medium">Action</th>
            </tr>
          </thead>
          <tbody>
            @for (quote of quotesForFollowup; track quote.id) {
              <tr class="border-b border-border last:border-0 even:bg-surface-raised/50 hover:bg-surface-raised transition-colors">
                <td class="px-5 h-11 font-mono text-[12.5px] font-medium"
                    style="color: rgb(var(--ds-accent, 37 99 235))">
                  {{ quote.quoteNumber }}
                </td>
                <td class="px-4 h-11 font-medium">{{ quote.clientName }}</td>
                <td class="px-4 h-11 text-xs"
                    [class.text-red-600]="quote.isOverdue"
                    [class.text-muted-foreground]="!quote.isOverdue">
                  {{ quote.sentDate | date:'mediumDate' }}
                </td>
                <td class="px-4 h-11">
                  @if (quote.isOverdue) {
                    <app-status-badge variant="danger" label="Overdue" />
                  } @else {
                    <app-status-badge variant="info" label="Pending" />
                  }
                </td>
                <td class="px-5 h-11 text-right">
                  <button hlmBtn variant="outline" size="sm" class="h-7 gap-1.5 text-xs"
                          (click)="followUp(quote.id)">
                    <ng-icon hlm name="lucidePhone" class="size-3" />
                    Follow-up
                  </button>
                </td>
              </tr>
            }
          </tbody>
        </table>
      </div>
    }
  </div>

</div>
